/**
 * StanceGauge - Horizontal bull/bear intensity bar with stance label.
 *
 * Green fills from left (bull intensity), red fills from right (bear risk).
 * Shows stance label and numeric value.
 */

import { cn } from '@/lib/utils';
import type { Stance } from '@/types/portfolio';

interface StanceGaugeProps {
  bullIntensity: number;
  bearRisk: number;
  stance: Stance;
  size?: 'sm' | 'md';
  className?: string;
}

const stanceConfig: Record<Stance, { label: string; color: string }> = {
  BULLISH: { label: 'Bullish', color: 'text-green-400' },
  LEANING_BULLISH: { label: 'Lean Bull', color: 'text-green-300' },
  NEUTRAL: { label: 'Neutral', color: 'text-text-muted' },
  LEANING_BEARISH: { label: 'Lean Bear', color: 'text-red-300' },
  BEARISH: { label: 'Bearish', color: 'text-red-400' },
};

export function StanceGauge({
  bullIntensity,
  bearRisk,
  stance,
  size = 'md',
  className,
}: StanceGaugeProps) {
  const config = stanceConfig[stance] || stanceConfig.NEUTRAL;
  const barHeight = size === 'sm' ? 'h-1.5' : 'h-2';

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Bar */}
      <div className={cn('flex-1 relative rounded-full overflow-hidden bg-surface-light', barHeight)}>
        {/* Bull fill (from left) */}
        <div
          className="absolute inset-y-0 left-0 bg-green-500/70 rounded-l-full transition-all duration-500"
          style={{ width: `${Math.min(bullIntensity, 100)}%` }}
        />
        {/* Bear fill (from right) */}
        <div
          className="absolute inset-y-0 right-0 bg-red-500/70 rounded-r-full transition-all duration-500"
          style={{ width: `${Math.min(bearRisk, 100)}%` }}
        />
      </div>

      {/* Label */}
      <span className={cn(
        'text-xs font-medium whitespace-nowrap',
        size === 'sm' ? 'min-w-[60px]' : 'min-w-[72px]',
        config.color,
      )}>
        {config.label}
      </span>
    </div>
  );
}
